import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { EligibilityVerification } from '@kp/eligibility/eligibility-verification.model';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { EligibilityVerificationType } from '@ktypes/enums';
import { DateTimeUtil } from '@kutil';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface PersonalDetailsFormValues {
  firstName: string;
  lastName: string;
  postalCode: string;
  dobMonth: number;
  dobDay: number;
  dobYear: number;
}

@Component({
  selector: 'kp-eligibility-personal-details',
  templateUrl: './eligibility-personal-details.component.html',
  styleUrls: ['./eligibility-personal-details.component.scss'],
})
export class EligibilityPersonalDetailsComponent implements OnInit, OnDestroy {
  @Input() error: boolean;
  @Input() loading: boolean;

  @Output() errorEmitter = new EventEmitter<boolean>();
  @Output() loadingEmitter = new EventEmitter<boolean>();

  eligibilityForm: UntypedFormGroup;
  months = DateTimeUtil.monthArray();

  private _destroy$ = new Subject<void>();

  // Personal form control
  dayError = false;
  dobMonth: UntypedFormControl;
  firstNameError = false;
  lastNameError = false;
  postalError = false;
  yearError = false;
  private _dobDay: UntypedFormControl;
  private _dobYear: UntypedFormControl;
  private _firstName: UntypedFormControl;
  private _lastName: UntypedFormControl;
  private _postalCode: UntypedFormControl;

  constructor(
    private _analyticsBloc: AnalyticsBloc,
    public groupValidationBloc: GroupValidationBloc,
    private _onboardingBloc: OnboardingBloc,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this._firstName = new UntypedFormControl(this._firstName);
    this._lastName = new UntypedFormControl(this._lastName);
    this._postalCode = new UntypedFormControl(this._postalCode, Validators.pattern(/^(\d{5})$/));
    this.months.unshift({ name: 'Select', number: null });
    this.dobMonth = new UntypedFormControl(this.dobMonth, [
      Validators.pattern(/^\d{1,2}$/),
      Validators.min(1),
      Validators.max(12),
    ]);
    this._dobDay = new UntypedFormControl(this._dobDay, [
      Validators.pattern(/^\d{1,2}$/),
      Validators.min(1),
      Validators.max(31),
    ]);
    this._dobYear = new UntypedFormControl(this._dobYear, [
      Validators.pattern(/^(\d{4})$/),
      Validators.min(1900),
      Validators.max(new Date().getFullYear() - 13),
    ]);
    this.eligibilityForm = new UntypedFormGroup({
      firstName: this._firstName,
      lastName: this._lastName,
      postalCode: this._postalCode,
      dobMonth: this.dobMonth,
      dobDay: this._dobDay,
      dobYear: this._dobYear,
    });
    this.eligibilityForm.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (value != null) {
        this.error = false;
        this.loading = false;
        this.errorEmitter.emit(false);
        this.loadingEmitter.emit(false);
      }
      this.firstNameError = (this._firstName.touched || this._firstName.dirty) && this._firstName.invalid;
      this.lastNameError = (this._lastName.touched || this._lastName.dirty) && this._lastName.invalid;
      this.dayError = (this._dobDay.touched || this._dobDay.dirty) && this._dobDay.invalid;
      this.yearError = (this._dobYear.touched || this._dobYear.dirty) && this._dobYear.invalid;
      this.postalError = (this._postalCode.touched || this._postalCode.dirty) && this._postalCode.invalid;
    });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._firstName = null;
    this._lastName = null;
    this._postalCode = null;
    this.dobMonth = null;
    this._dobDay = null;
    this._dobYear = null;
    this.error = false;
    this.loading = false;
    this.errorEmitter.emit(false);
    this.loadingEmitter.emit(false);
  }

  onSubmit(formValues: PersonalDetailsFormValues) {
    if (this.eligibilityForm.valid) {
      this.loading = true;
      this.loadingEmitter.emit(true);
      const dobFormValue = DateTimeUtil.formatDate(
        new Date(formValues.dobYear, formValues.dobMonth - 1, formValues.dobDay)
      );
      const eligibilityVerification = new EligibilityVerification().deserialize({
        verificationType: EligibilityVerificationType.postal_dob_name,
        firstName: formValues?.firstName.trim(),
        lastName: formValues?.lastName.trim(),
        postalCode: formValues.postalCode,
        dateOfBirth: dobFormValue,
      });
      this._onboardingBloc.verifyEligibility(eligibilityVerification);
    }
  }

  switchToAnotherMethod() {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.eligibility,
        event: AnalyticEvent.eligibility_method_switched,
      })
    );
    // Needed to clear out error message when switching between types
    this._onboardingBloc.clearEligibilityStream();
  }

  blurUpdate(formValues: PersonalDetailsFormValues) {
    if (formValues.dobYear?.toString().length === 2) {
      const year = new Date().getFullYear() - 2000;
      if (formValues.dobYear >= year) {
        this._dobYear.setValue(`19${formValues.dobYear.toString()}`);
      } else {
        this._dobYear.setValue(`20${formValues.dobYear.toString()}`);
      }
    }
  }
}
